import {Component} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {routes} from '../app-routing.module';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {KeycloakService} from "keycloak-angular";
import {ApiGatewayService} from '../services/api-gateway.service';
import {RouteAuthorizationService} from '../services/route-authorization.service';
import {MotdService} from '../services/motd.service';
import {environment} from "../../environments/environment.production";

@Component({
    selector: 'top-nav',
    templateUrl: './top-nav.component.html',
    standalone: false
})
export class TopNavComponent {
  links: any[] = [];
  activeLink?: string;
  activeTitle?: string;
  parentLink?: { path: string, name: string };
  isExpanded = true;
  isExpandedMobile = false;
  isDark = false;
  hiddenMessageCount = 0;
  envVersion : string = environment.version;

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    protected readonly keycloak: KeycloakService,
    private routeAuthService: RouteAuthorizationService,
    private gatewayApiService: ApiGatewayService,
    private motdService: MotdService) {

    this.initializeLinks().then(() => {
      this.updateActiveTitle();
    });

    router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateActiveTitle();
        this.isExpandedMobile = false;
      }
    });

    this.motdService.motdState$.subscribe(state => {
      this.hiddenMessageCount = state.hiddenCount;
    });
  }

  private updateActiveTitle() {
    this.activeLink = this.router.url;
    const path = this.router.url.split(/[?#]/)[0];
    const current = routes.find(route => '/' + route.path === path);
    const parent = routes.find(route => route.path && path.startsWith('/' + route.path + '/'));
    this.parentLink = parent && { path: '/' + parent.path, name: parent.title as string };
    this.activeTitle = current?.title as string || 'Dashboard';
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  toggleExpandMobile() {
    this.isExpandedMobile = !this.isExpandedMobile;
  }

  isMenuExpanded() {
    return this.isExpanded ? 'menu-open' : 'menu-closed w-[80px]';
  }

  public getBackgroundClass(): string {
    // Extract route path from URL (remove leading slash and query params)
    const path = this.router.url.split('?')[0].replace('/', '');

    // Map specific routes to their backgrounds
    const backgroundMap: { [key: string]: string } = {
      'devices': 'bg-[url(/assets/bg_devices.png)]',
      'pass_prediction': 'bg-[url(/assets/bg_pass_prediction.png)]',
    };

    // Return custom background if route matches, otherwise use default
    const customBg = backgroundMap[path];
    if (customBg) {
      return `${customBg} bg-no-repeat bg-bottom-right`;
    }
    return ''
  }

  getIconForLink(name: string): SafeHtml {
    const iconMap: { [key: string]: string } = {
      'Dashboard': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M5 4H9C9.26522 4 9.51957 4.10536 9.70711 4.29289C9.89464 4.48043 10 4.73478 10 5V11C10 11.2652 9.89464 11.5196 9.70711 11.7071C9.51957 11.8946 9.26522 12 9 12H5C4.73478 12 4.48043 11.8946 4.29289 11.7071C4.10536 11.5196 4 11.2652 4 11V5C4 4.73478 4.10536 4.48043 4.29289 4.29289C4.48043 4.10536 4.73478 4 5 4ZM5 16H9C9.26522 16 9.51957 16.1054 9.70711 16.2929C9.89464 16.4804 10 16.7348 10 17V19C10 19.2652 9.89464 19.5196 9.70711 19.7071C9.51957 19.8946 9.26522 20 9 20H5C4.73478 20 4.48043 19.8946 4.29289 19.7071C4.10536 19.5196 4 19.2652 4 19V17C4 16.7348 4.10536 16.4804 4.29289 16.2929C4.48043 16.1054 4.73478 16 5 16ZM15 12H19C19.2652 12 19.5196 12.1054 19.7071 12.2929C19.8946 12.4804 20 12.7348 20 13V19C20 19.2652 19.8946 19.5196 19.7071 19.7071C19.5196 19.8946 19.2652 20 19 20H15C14.7348 20 14.4804 19.8946 14.2929 19.7071C14.1054 19.5196 14 19.2652 14 19V13C14 12.7348 14.1054 12.4804 14.2929 12.2929C14.4804 12.1054 14.7348 12 15 12ZM15 4H19C19.2652 4 19.5196 4.10536 19.7071 4.29289C19.8946 4.48043 20 4.73478 20 5V7C20 7.26522 19.8946 7.51957 19.7071 7.70711C19.5196 7.89464 19.2652 8 19 8H15C14.7348 8 14.4804 7.89464 14.2929 7.70711C14.1054 7.51957 14 7.26522 14 7V5C14 4.73478 14.1054 4.48043 14.2929 4.29289C14.4804 4.10536 14.7348 4 15 4Z" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>',
      'Pass Prediction': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M3.70685 6.293L6.29285 3.707C6.48038 3.51953 6.73468 3.41422 6.99985 3.41422C7.26501 3.41422 7.51932 3.51953 7.70685 3.707L13.2928 9.293C13.4803 9.48053 13.5856 9.73484 13.5856 10C13.5856 10.2652 13.4803 10.5195 13.2928 10.707L10.7068 13.293C10.5193 13.4805 10.265 13.5858 9.99985 13.5858C9.73468 13.5858 9.48038 13.4805 9.29285 13.293L3.70685 7.707C3.51938 7.51947 3.41406 7.26517 3.41406 7C3.41406 6.73484 3.51938 6.48053 3.70685 6.293Z" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M6 10L3 13L6 16L9 13M10 6L13 3L16 6L13 9M12 12L13.5 13.5M14.5 17C15.163 17 15.7989 16.7366 16.2678 16.2678C16.7366 15.7989 17 15.163 17 14.5M15 21C16.5913 21 18.1174 20.3679 19.2426 19.2426C20.3679 18.1174 21 16.5913 21 15" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>',
      'Nodes': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M11 20H5C4.46957 20 3.96086 19.7893 3.58579 19.4142C3.21071 19.0391 3 18.5304 3 18V6C3 5.46957 3.21071 4.96086 3.58579 4.58579C3.96086 4.21071 4.46957 4 5 4H19C19.5304 4 20.0391 4.21071 20.4142 4.58579C20.7893 4.96086 21 5.46957 21 6V13M15 19L17 21L21 17M9 17H11.5" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>',
      'Service Status': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M6.99996 18C5.75223 18 4.55561 17.5259 3.67334 16.682C2.79107 15.8381 2.29541 14.6935 2.29541 13.5C2.29541 12.3065 2.79107 11.1619 3.67334 10.318C4.55561 9.47412 5.75223 9.00001 6.99996 9.00001C7.29464 7.68719 8.15672 6.5335 9.39654 5.79273C10.0104 5.42594 10.6986 5.17156 11.4217 5.04412C12.1449 4.91669 12.8888 4.91869 13.6111 5.05001C14.3333 5.18133 15.0198 5.43941 15.6312 5.80949C16.2427 6.17958 16.7672 6.65443 17.1747 7.20694C17.5823 7.75945 17.8649 8.37879 18.0065 9.02961C18.1481 9.68043 18.1459 10.35 18 11H19C19.9282 11 20.8185 11.3688 21.4748 12.0251C22.1312 12.6815 22.5 13.5718 22.5 14.5C22.5 15.4283 22.1312 16.3185 21.4748 16.9749C20.8185 17.6313 19.9282 18 19 18H18" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M9 15L12 12M12 12L15 15M12 12V21" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>',
      'Almanac Tools': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M12 20H6C5.46957 20 4.96086 19.7893 4.58579 19.4142C4.21071 19.0391 4 18.5304 4 18V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H18V9" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M13 16H6C5.46957 16 4.96086 16.2107 4.58579 16.5858C4.21071 16.9609 4 17.4696 4 18M15 19L18 22M18 22L21 19M18 22V13" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>',
      'Docs': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2H19C19.2652 2 19.5196 2.10536 19.7071 2.29289C19.8946 2.48043 20 2.73478 20 3V21C20 21.2652 19.8946 21.5196 19.7071 21.7071C19.5196 21.8946 19.2652 22 19 22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5ZM4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20107 17.2634 5.83696 17 6.5 17H20" stroke="#404040" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>',
      'Resources': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M14 3V7C14 7.26522 14.1054 7.51957 14.2929 7.70711C14.4804 7.89464 14.7348 8 15 8H19" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M12 17V11M17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H14L19 8V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21Z" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M9.5 14.5L12 17L14.5 14.5" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>',
      'Subscription': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M4 21V4C4 3.73478 4.10536 3.48043 4.29289 3.29289C4.48043 3.10536 4.73478 3 5 3H19C19.2652 3 19.5196 3.10536 19.7071 3.29289C19.8946 3.48043 20 3.73478 20 4V21L17 19L14 21L12 19L10 21L7 19L4 21Z" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M8 8H16M8 12H16" stroke="#525252" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>',
      'ADS': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M4.9001 16.0999C1.0001 12.1999 1.0001 5.7999 4.9001 1.8999" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M7.7999 4.69995C6.85121 5.68293 6.25742 6.95422 6.11252 8.31263C5.96763 9.67104 6.27989 11.039 6.9999 12.2" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M12 11C13.1046 11 14 10.1046 14 9C14 7.89543 13.1046 7 12 7C10.8954 7 10 7.89543 10 9C10 10.1046 10.8954 11 12 11Z" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M16.2 4.80005C18.2 6.80005 18.46 9.91005 17 12.27" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M19.1001 1.8999C20.0271 2.82504 20.7626 3.92393 21.2644 5.13366C21.7662 6.34339 22.0245 7.64022 22.0245 8.9499C22.0245 10.2596 21.7662 11.5564 21.2644 12.7661C20.7626 13.9759 20.0271 15.0748 19.1001 15.9999" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M9.5 18H14.5" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '<path d="M8 22L12 11L16 22" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\n' +
        '</svg>\n'
    };
    const svg = iconMap[name] || '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" fill="currentColor"/></svg>';
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  private async featureFlags(): Promise<Record<string, boolean>> {
    try {
      const response = await this.gatewayApiService.getUserProfile();
      return response.body?.features ?? {};
    } catch {
      return {};
    }
  }

  async initializeLinks() {
    const features = await this.featureFlags();
    for (const route of routes || []) {
      if (route.data && route.data[0] >= 0 && route.path) {
        const featureKey = route.data[3] as string | undefined;
        if (featureKey && !features[featureKey]) {
          continue;
        }
        const canActivate = await this.routeAuthService.checkCanActivate(route);
        if (canActivate) {
          this.links.push({
            path: '/' + route.path,
            name: <string>route.title,
            external: route.data[2] as string | undefined,
          });
        }
      }
    }
  }

  toggleLightDark() {
    const topElement = document.body;
    this.isDark = !this.isDark;
    if (topElement) {
      topElement.classList.toggle('dark');

      // Toggle data-theme attribute
      const currentTheme = topElement.getAttribute('data-theme');
      if (currentTheme === 'dark') {
        topElement.setAttribute('data-theme', 'fantasy');
      } else {
        topElement.setAttribute('data-theme', 'dark');
      }
    }
    this.isExpandedMobile = false;
  }

  Logout() {

    console.log("Keycloak logout")
    this.keycloak.logout();

  }

  ShowMOTD() {
    this.motdService.clearHiddenMessages();
  }

  hasHiddenMessages(): boolean {
    return this.hiddenMessageCount > 0;
  }
}
